import { useEffect, type ReactNode } from 'react';
import type { SlideDirection } from '../hooks/useMeasurePagination';
import { totalSlidingPages } from '../utils/measurePages';
import { pagerLabel as buildPagerLabel } from '../utils/pagerLabel';

interface Props {
  page: number;
  pages: number;
  slide: SlideDirection;
  onSlideEnd: () => void;
  showPager: boolean;
  measureCount?: number;
  windowStart?: number;
  visibleCount?: number;
  onPrevPage?: () => void;
  onNextPage?: () => void;
  children: ReactNode;
}

export function MeasurePageView({
  page,
  pages,
  slide,
  onSlideEnd,
  showPager,
  measureCount = 1,
  windowStart = 0,
  visibleCount,
  onPrevPage,
  onNextPage,
  children,
}: Props) {
  useEffect(() => {
    if (slide === 'none') return;
    const t = setTimeout(onSlideEnd, 480);
    return () => clearTimeout(t);
  }, [slide, page, onSlideEnd]);

  return (
    <div className="measure-page-wrap">
      {showPager && pages > 1 && (
        <div className="measure-pager" aria-live="polite">
          {(onPrevPage || onNextPage) && (
            <button
              type="button"
              className="measure-pager__nav"
              onClick={onPrevPage}
              disabled={page <= 0}
              aria-label="Compases anteriores"
            >
              ‹
            </button>
          )}
          <span className="measure-pager__label">
            {buildPagerLabel(windowStart, measureCount, visibleCount)}
          </span>
          <div className="measure-pager__dots">
            {Array.from({ length: pages }, (_, i) => (
              <span
                key={i}
                className={`measure-pager__dot ${i === page ? 'measure-pager__dot--active' : ''}`}
              />
            ))}
          </div>
          {(onPrevPage || onNextPage) && (
            <button
              type="button"
              className="measure-pager__nav"
              onClick={onNextPage}
              disabled={page >= pages - 1}
              aria-label="Compases siguientes"
            >
              ›
            </button>
          )}
        </div>
      )}
      <div
        key={`page-${windowStart}-${slide}`}
        className={`measure-page measure-page--${slide}`}
        onAnimationEnd={onSlideEnd}
      >
        {children}
      </div>
    </div>
  );
}

export { buildPagerLabel as pagerLabel, totalSlidingPages as totalPages };
